import React, { useEffect, useState, useCallback } from 'react';
import { getTrades, getDataSectors } from '../api';
import { usePersistentPageSize } from '../hooks/usePersistentPageSize';

const S = {
  card: {
    background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: 10, padding: '16px 20px',
  },
  toolbar: { display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' },
  tabBar: { display: 'flex', gap: 0, marginBottom: 20, borderBottom: '2px solid #E5E7EB' },
  tab: (active) => ({
    padding: '8px 20px', cursor: 'pointer', border: 'none', background: 'transparent',
    fontWeight: active ? 700 : 400, fontSize: 14,
    color: active ? '#2563EB' : '#6B7280',
    borderBottom: active ? '2px solid #2563EB' : '2px solid transparent',
    marginBottom: -2,
  }),
  filterBtn: (active) => ({
    padding: '5px 14px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 12,
    background: active ? '#2563EB' : '#F3F4F6', color: active ? '#FFFFFF' : '#374151',
    fontWeight: active ? 700 : 400,
  }),
  tableWrap: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: {
    background: '#F9FAFB', color: '#6B7280', textAlign: 'left',
    padding: '8px 12px', borderBottom: '1px solid #E5E7EB', fontWeight: 500, whiteSpace: 'nowrap',
  },
  td: { padding: '7px 12px', borderBottom: '1px solid #F3F4F6', whiteSpace: 'nowrap' },
  pager: {
    display: 'flex', gap: 8, justifyContent: 'center', marginTop: 16, alignItems: 'center', flexWrap: 'wrap',
  },
  pgBtn: (active, disabled) => ({
    padding: '5px 12px', borderRadius: 6, border: 'none', cursor: disabled ? 'not-allowed' : 'pointer',
    background: active ? '#2563EB' : '#F3F4F6',
    color: active ? '#FFFFFF' : disabled ? '#9CA3AF' : '#374151',
    fontWeight: active ? 700 : 400,
    opacity: disabled ? 0.5 : 1,
  }),
  psBtn: (active) => ({
    padding: '3px 10px', borderRadius: 6, border: `1px solid ${active ? '#2563EB' : '#E5E7EB'}`,
    cursor: 'pointer', fontSize: 12,
    background: active ? '#2563EB' : 'transparent',
    color: active ? '#fff' : '#6B7280',
    fontWeight: active ? 700 : 400,
  }),
  badge: (positive) => ({
    display: 'inline-block', padding: '2px 8px', borderRadius: 12, fontSize: 12, fontWeight: 700,
    background: positive ? 'rgba(22,163,74,0.1)' : 'rgba(220,38,38,0.1)',
    color: positive ? '#16A34A' : '#DC2626',
  }),
};

const PAGE_SIZE_OPTIONS = [25, 50, 100, 250, 'all'];
const DEFAULT_SECTORS = [
  'Basic Materials', 'Communication Services', 'Consumer Cyclical',
  'Consumer Defensive', 'Energy', 'Financial Services', 'Healthcare',
  'Industrials', 'Real Estate', 'Technology', 'Utilities',
];

function fmtTs(ts) {
  if (!ts) return '—';
  return String(ts).slice(0, 16).replace('T', ' ');
}

function fmtUsd(val) {
  if (val == null) return '—';
  return `$${Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// ─── Закриті угоди (тільки SELL, відображаються як пари вхід+вихід) ───────────
function ClosedTrades({ sector, page, setPage, pageSize, setPageSize, onTickerClick }) {
  const [rows, setRows]     = useState([]);
  const [total, setTotal]   = useState(0);
  const [loading, setLoading] = useState(false);

  const effectiveLimit = pageSize === 'all' ? 10000 : pageSize;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: effectiveLimit, offset: page * effectiveLimit, action: 'SELL' };
      if (sector) params.sector = sector;
      const res = await getTrades(params);
      setRows(res.data.trades || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      console.error('Trades error:', e);
    } finally {
      setLoading(false);
    }
  }, [page, sector, effectiveLimit]);

  useEffect(() => { load(); }, [load]);

  const totalPages = pageSize === 'all' ? 1 : Math.max(1, Math.ceil(total / pageSize));

  // Підсумок по закритих угодах
  const totalPnl = rows.reduce((acc, t) => acc + (t.pnl_usd || 0), 0);
  const wins     = rows.filter(t => t.pnl_usd != null && t.pnl_usd >= 0).length;
  const winRate  = rows.length > 0 ? ((wins / rows.length) * 100).toFixed(1) : null;

  return (
    <>
      {/* Summary bar */}
      {rows.length > 0 && (
        <div style={{ display: 'flex', gap: 24, marginBottom: 16, padding: '10px 0', borderBottom: '1px solid #F3F4F6' }}>
          <span style={{ fontSize: 13, color: '#6B7280' }}>
            Показано: <strong style={{ color: '#101828' }}>{rows.length}</strong> з {total}
          </span>
          <span style={{ fontSize: 13, color: '#6B7280' }}>
            Win rate: <strong style={{ color: winRate >= 60 ? '#16A34A' : '#DC2626' }}>{winRate}%</strong>
          </span>
          <span style={{ fontSize: 13, color: '#6B7280' }}>
            P&L на сторінці:{' '}
            <strong style={{ color: totalPnl >= 0 ? '#16A34A' : '#DC2626' }}>
              {totalPnl >= 0 ? '+' : ''}{fmtUsd(totalPnl)}
            </strong>
          </span>
        </div>
      )}

      {loading ? (
        <div style={{ color: '#6B7280', padding: 24 }}>Завантаження…</div>
      ) : (
        <>
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead>
                <tr>
                  {['Тікер', 'К-ть', 'Ціна входу', 'Ціна виходу', 'Сума входу', 'Сума виходу', 'P&L $', 'P&L %', 'Дата закриття', 'Причина'].map(h => (
                    <th key={h} style={S.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map(t => {
                  const profit    = t.pnl_usd != null && t.pnl_usd >= 0;
                  const exitTotal = t.total_usd;
                  const entryTotal = t.pnl_usd != null ? t.total_usd - t.pnl_usd : null;
                  const entryPrice = (entryTotal != null && t.quantity > 0)
                    ? entryTotal / t.quantity
                    : null;
                  const rowBg = profit ? 'rgba(22,163,74,0.04)' : 'rgba(220,38,38,0.04)';

                  return (
                    <tr key={t.id} style={{ background: rowBg }}>
                      <td style={{ ...S.td, fontWeight: 700, color: '#2563EB', cursor: 'pointer' }}
                          onClick={() => onTickerClick && onTickerClick(t.ticker)}>
                        {t.ticker}
                      </td>
                      <td style={S.td}>{t.quantity}</td>
                      <td style={{ ...S.td, color: '#6B7280' }}>
                        {entryPrice != null ? `$${Number(entryPrice).toFixed(2)}` : '—'}
                      </td>
                      <td style={{ ...S.td, color: '#101828', fontWeight: 600 }}>
                        ${Number(t.price).toFixed(2)}
                      </td>
                      <td style={{ ...S.td, color: '#6B7280' }}>
                        {fmtUsd(entryTotal)}
                      </td>
                      <td style={{ ...S.td, fontWeight: 600 }}>
                        {fmtUsd(exitTotal)}
                      </td>
                      <td style={S.td}>
                        {t.pnl_usd != null
                          ? <span style={S.badge(profit)}>{profit ? '+' : ''}{fmtUsd(t.pnl_usd)}</span>
                          : '—'}
                      </td>
                      <td style={S.td}>
                        {t.pnl_pct != null
                          ? <span style={S.badge(profit)}>{profit ? '+' : ''}{Number(t.pnl_pct).toFixed(2)}%</span>
                          : '—'}
                      </td>
                      <td style={{ ...S.td, color: '#6B7280' }}>{fmtTs(t.timestamp)}</td>
                      <td style={{ ...S.td, color: '#6B7280', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {t.reason || '—'}
                      </td>
                    </tr>
                  );
                })}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={10} style={{ ...S.td, color: '#6B7280', textAlign: 'center', padding: 32 }}>
                      Немає закритих угод
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <Pager page={page} setPage={setPage} totalPages={totalPages} pageSize={pageSize} setPageSize={setPageSize} />
        </>
      )}
    </>
  );
}

// ─── Журнал ордерів (всі BUY та SELL окремо) ─────────────────────────────────
function OrderLog({ sector, page, setPage, pageSize, setPageSize, onTickerClick }) {
  const [rows, setRows]       = useState([]);
  const [total, setTotal]     = useState(0);
  const [filter, setFilter]   = useState('Всі');
  const [loading, setLoading] = useState(false);

  const effectiveLimit = pageSize === 'all' ? 10000 : pageSize;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: effectiveLimit, offset: page * effectiveLimit };
      if (filter !== 'Всі') params.action = filter;
      if (sector) params.sector = sector;
      const res = await getTrades(params);
      setRows(res.data.trades || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      console.error('Trades error:', e);
    } finally {
      setLoading(false);
    }
  }, [page, filter, sector, effectiveLimit]);

  useEffect(() => { load(); }, [load]);

  function handleFilter(f) { setFilter(f); setPage(0); }

  const totalPages = pageSize === 'all' ? 1 : Math.max(1, Math.ceil(total / pageSize));

  return (
    <>
      <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
        {['Всі', 'BUY', 'SELL'].map(f => (
          <button key={f} style={S.filterBtn(filter === f)} onClick={() => handleFilter(f)}>{f}</button>
        ))}
        <span style={{ marginLeft: 8, color: '#9CA3AF', fontSize: 12, alignSelf: 'center' }}>
          всього {total}
        </span>
      </div>

      {loading ? (
        <div style={{ color: '#6B7280', padding: 24 }}>Завантаження…</div>
      ) : (
        <>
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead>
                <tr>
                  {['Час', 'Тікер', 'Дія', 'К-ть', 'Ціна', 'Сума', 'P&L $', 'P&L %', 'Причина'].map(h => (
                    <th key={h} style={S.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map(t => {
                  const isBuy  = t.action === 'BUY';
                  const pnlPos = t.pnl_pct != null && t.pnl_pct >= 0;
                  const rowBg  = isBuy
                    ? 'rgba(37,99,235,0.04)'
                    : pnlPos ? 'rgba(22,163,74,0.05)' : 'rgba(220,38,38,0.05)';

                  return (
                    <tr key={t.id} style={{ background: rowBg }}>
                      <td style={{ ...S.td, color: '#6B7280' }}>{fmtTs(t.timestamp)}</td>
                      <td style={{ ...S.td, fontWeight: 700, color: '#2563EB', cursor: 'pointer' }}
                          onClick={() => onTickerClick && onTickerClick(t.ticker)}>
                        {t.ticker}
                      </td>
                      <td style={{ ...S.td, fontWeight: 700, color: isBuy ? '#2563EB' : (pnlPos ? '#16A34A' : '#DC2626') }}>
                        {t.action}
                      </td>
                      <td style={S.td}>{t.quantity}</td>
                      <td style={S.td}>${Number(t.price).toFixed(2)}</td>
                      <td style={S.td}>{fmtUsd(t.total_usd)}</td>
                      <td style={{ ...S.td, color: t.pnl_usd == null ? '#9CA3AF' : pnlPos ? '#16A34A' : '#DC2626', fontWeight: 600 }}>
                        {t.pnl_usd != null ? `${t.pnl_usd >= 0 ? '+' : ''}${fmtUsd(t.pnl_usd)}` : '—'}
                      </td>
                      <td style={{ ...S.td, color: t.pnl_pct == null ? '#9CA3AF' : pnlPos ? '#16A34A' : '#DC2626', fontWeight: 600 }}>
                        {t.pnl_pct != null ? `${t.pnl_pct >= 0 ? '+' : ''}${Number(t.pnl_pct).toFixed(2)}%` : '—'}
                      </td>
                      <td style={{ ...S.td, color: '#6B7280', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {t.reason || '—'}
                      </td>
                    </tr>
                  );
                })}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={9} style={{ ...S.td, color: '#6B7280', textAlign: 'center', padding: 32 }}>
                      Немає ордерів
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <Pager page={page} setPage={setPage} totalPages={totalPages} pageSize={pageSize} setPageSize={setPageSize} />
        </>
      )}
    </>
  );
}

// ─── Пагінатор ────────────────────────────────────────────────────────────────
function Pager({ page, setPage, totalPages, pageSize, setPageSize }) {
  return (
    <div style={S.pager}>
      {totalPages > 1 && (
        <>
          <button
            style={S.pgBtn(false, page === 0)}
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
          >← Попередня</button>
          <span style={{ color: '#6B7280', fontSize: 13 }}>
            Стор. {page + 1} / {totalPages}
          </span>
          <button
            style={S.pgBtn(false, page >= totalPages - 1)}
            onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
          >Наступна →</button>
          <span style={{ width: 1, background: '#E5E7EB', alignSelf: 'stretch', margin: '0 4px' }} />
        </>
      )}
      <span style={{ color: '#9CA3AF', fontSize: 12 }}>Рядків:</span>
      {PAGE_SIZE_OPTIONS.map(opt => {
        const val = opt === 'all' ? 'all' : opt;
        return (
          <button key={opt} style={S.psBtn(pageSize === val)} onClick={() => { setPageSize(val); setPage(0); }}>
            {opt === 'all' ? 'Всі' : opt}
          </button>
        );
      })}
    </div>
  );
}

// ─── Головний компонент ───────────────────────────────────────────────────────
export default function Trades({ navigateTo }) {
  const [activeTab, setActiveTab] = useState('closed');
  const [sector,    setSector]    = useState('');
  const [page,      setPage]      = useState(0);
  const [pageSize,  setPageSize]  = usePersistentPageSize('pg_trades', 50);
  const [availableSectors, setAvailableSectors] = useState(DEFAULT_SECTORS);

  useEffect(() => {
    getDataSectors().then(r => {
      if (Array.isArray(r.data) && r.data.length > 0) setAvailableSectors(r.data);
    }).catch(() => {});
  }, []);

  function handleSector(s)  { setSector(prev => prev === s ? '' : s); setPage(0); }
  function handleTab(t)     { setActiveTab(t); setPage(0); }
  function handleTickerClick(ticker) { if (navigateTo) navigateTo('/trades/ticker/' + ticker); }

  return (
    <div style={S.card}>
      {/* Вкладки */}
      <div style={S.tabBar}>
        <button style={S.tab(activeTab === 'closed')} onClick={() => handleTab('closed')}>
          Закриті угоди
        </button>
        <button style={S.tab(activeTab === 'orders')} onClick={() => handleTab('orders')}>
          Журнал ордерів
        </button>
      </div>

      {/* Фільтр по секторах */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
        {availableSectors.map(s => (
          <button key={s} style={S.filterBtn(sector === s)} onClick={() => handleSector(s)}>{s}</button>
        ))}
        {sector && (
          <button style={S.filterBtn(false)} onClick={() => setSector('')}>✕ Скинути</button>
        )}
      </div>

      {activeTab === 'closed' && (
        <ClosedTrades
          sector={sector}
          page={page} setPage={setPage}
          pageSize={pageSize} setPageSize={setPageSize}
          onTickerClick={handleTickerClick}
        />
      )}
      {activeTab === 'orders' && (
        <OrderLog
          sector={sector}
          page={page} setPage={setPage}
          pageSize={pageSize} setPageSize={setPageSize}
          onTickerClick={handleTickerClick}
        />
      )}
    </div>
  );
}
